import type { FC } from "react"
import { Box, IconButton, Stack, Typography } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import type { Control, FieldValues, UseFieldArrayRemove } from "react-hook-form"
import { useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"

import type { ThresholdsField } from "types/alerts.types"
import type { InputRules } from "types/common.types"

import ControlledInput from "widgets/common/ControlledInput"
import RangeInput from "./RangeInput"
import { getRangeDropdownOptions } from "./thresholds"

interface ThresholdInputProps {
  remove: UseFieldArrayRemove
  rules?: InputRules
  statusDropdownOptions: { value: string; label: string }[] | undefined
  index?: number
  control: Control<FieldValues, any>
  value: ThresholdsField
  unit?: string
}

const ThresholdInput: FC<ThresholdInputProps> = ({
  remove,
  index,
  rules,
  statusDropdownOptions,
  value,
  unit,
}) => {
  const { control, register } = useFormContext()
  const { t } = useTranslation()

  const { rangeDropdownOptions, defaultRangeCondition } = getRangeDropdownOptions(t)
  const defaultAlertStatus = statusDropdownOptions?.[0]?.label
  const currentRangeCondition = useWatch({
    control,
    name: `thresholds.${index}.rangeCondition`,
    defaultValue: value.rangeCondition || defaultRangeCondition,
  })

  return (
    <>
      <IconButton
        size="small"
        onClick={() => remove(index)}
        sx={{ position: "absolute", top: 6, right: 6 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <Stack sx={{ gap: 1.25 }}>
        <Typography variant="body2" color="text.secondary">
          {t("alert_settings.WHEN_VALUE")}
        </Typography>
        <ControlledInput
          type="select"
          placeholder=""
          {...register(`thresholds.${index}.rangeCondition`)}
          // name={`thresholds[${index}].rangeCondition`}
          selectOptions={rangeDropdownOptions}
          defaultValue={defaultRangeCondition}
          variant="outlined"
          size="small"
          rules={rules}
        />
        <RangeInput rangeCondition={currentRangeCondition} index={index} unit={unit} />
        <Stack sx={{ flexDirection: "row", alignItems: "center", gap: 1.25 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ whiteSpace: "nowrap" }}
          >
            {t("alert_settings.SET_STATUS_TO")}
          </Typography>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <ControlledInput
              type="select"
              placeholder="Status"
              // name={`alertStatus[${index}]`}
              selectOptions={statusDropdownOptions}
              defaultValue={defaultAlertStatus}
              {...register(`thresholds.${index}.alertStatus`)}
              variant="outlined"
              size="small"
              rules={rules}
            />
          </Box>
        </Stack>
      </Stack>
    </>
  )
}

export default ThresholdInput
